import { Button, Col, DatePicker, Form, Input, message, Row, Space } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import React, { useEffect, useState } from 'react';
import ButtonGroup from './ButtonGroup';
import moment from 'moment';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { saveConfigInfo } from '@app/api/pre-clearance.api';

type ConfigurationProps = {
  editable: boolean;
  isStatusDoReview: boolean;
  acceptedFields: any;
  status: string | null;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setAcceptedFields: Function;
  form: any;
  preClearanceData: any;
  extensionExpire?: string | null; // Add the prop for extensionExpire
  updateConfigInfo?: boolean;
  pre_clearance_id?: string | null; // Add the prop for pre_clearance_id
};

const Configuration: React.FC<ConfigurationProps> = ({
  editable,
  isStatusDoReview,
  acceptedFields = {},
  status,
  setAcceptedFields,
  form,
  preClearanceData,
  extensionExpire, // Destructure the new prop here
  updateConfigInfo, // Destructure the new prop here
  pre_clearance_id,
}) => {
  const [disabledButtons, setDisabledButtons] = useState<Record<string, boolean>>({});
  const [visibleButtons, setButtonVisibility] = useState<Record<string, string>>({});
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (preClearanceData) {
      form.setFieldsValue({
        product_name: preClearanceData?.product_name || '',
        pre_clearance_category_id: preClearanceData?.pre_clearance_category_id || '',
        validity_period: preClearanceData?.validity_period || '',
        validity_exp: preClearanceData?.validity_exp || '',
        special_conditions: preClearanceData?.special_conditions || '',
        extension_expiration: extensionExpire || 'N/A', // Set the extension expiration field here
      });

      setAcceptedFields(preClearanceData.accepted || {});
    }
  }, [form, preClearanceData, setAcceptedFields, extensionExpire, updateConfigInfo]);

  console.log('updateConfigInfo:', updateConfigInfo);

  const handleUpdateConfig = async () => {
    try {
      const values = await form.validateFields();

      const payload = {
        validity_period: values.validity_period,
        validity_exp: values.validity_exp,
        special_conditions: values.special_conditions,
      };

      setDisabledButtons((prev) => ({ ...prev, update: true }));

      await saveConfigInfo(Number(pre_clearance_id), payload);

      message.success('Configuration updated successfully');

      // 🔥 switch back to view mode
      setIsEditing(false);
    } catch (error) {
      message.error('Failed to update configuration');
    } finally {
      setDisabledButtons((prev) => ({ ...prev, update: false }));
    }
  };

  const handleCancelEdit = () => {
    // reset back to original API values
    form.setFieldsValue({
      validity_period: preClearanceData?.validity_period || '',
      validity_exp: preClearanceData?.validity_exp || '',
      special_conditions: preClearanceData?.special_conditions || '',
    });

    setIsEditing(false);
  };

  const fields: any = [
    {
      name: 'validity_period',
      label: 'Validity Period',
      component: isEditing ? (
        <Input.Group compact style={{ display: 'flex', width: '100%' }}>
          <Input
            style={{ width: '100%' }}
            value={form.getFieldValue('validity_period')}
            onChange={(e) => form.setFieldsValue({ validity_period: e.target.value })}
            placeholder="Enter Validity Period"
          />
        </Input.Group>
      ) : (
        <BaseInput readOnly value={form.getFieldValue('validity_period')} />
      ),
    },
    {
      name: 'validity_exp',
      label: 'Validity Expiration',
      component: isEditing ? (
        <Input.Group compact style={{ display: 'flex', width: '100%' }}>
          <DatePicker
            style={{ width: '100%' }}
            value={form.getFieldValue('validity_exp') ? moment(form.getFieldValue('validity_exp')) : null}
            onChange={(date) => form.setFieldsValue({ validity_exp: date ? date.format('YYYY-MM-DD') : '' })}
            placeholder="Select Expiration Date"
            format="YYYY-MM-DD"
          />
        </Input.Group>
      ) : (
        <BaseInput
          readOnly
          value={form.getFieldValue('validity_exp') ? form.getFieldValue('validity_exp').split(' ')[0] : ''}
        />
      ),
    },
    {
      name: 'special_conditions',
      label: 'Special Conditions',
      component: isEditing ? (
        <Input.TextArea
          style={{ width: '100%' }}
          value={form.getFieldValue('special_conditions')}
          onChange={(e) => form.setFieldsValue({ special_conditions: e.target.value })}
          placeholder="Enter Special Conditions"
          rows={4}
        />
      ) : (
        <BaseInput readOnly value={form.getFieldValue('special_conditions')} />
      ),
    },
    // New field for extensionExpire (non-editable)
    // {
    //   name: 'extension_expiration',
    //   label: 'Extension Expiration',
    //   component: (
    //     <>
    //       <BaseInput readOnly value={extensionExpire || 'N/A'} />
    //     </>
    //   ),
    // },
  ];

  return (
    <>
      <Row style={{ marginBottom: 24 }}>
        <Col>
          {updateConfigInfo && !isEditing && (
            <BaseButton type="ghost" icon={<EditOutlined />} onClick={() => setIsEditing(true)}>
              Edit Config Info
            </BaseButton>
          )}
        </Col>
      </Row>
      <Row>
        {fields?.map((e: any) => (
          <React.Fragment key={e.name}>
            <Col xs={24} md={24} lg={12}>
              <Form.Item
                name={e?.name}
                label={
                  <Space style={{ display: 'flex', alignItems: 'center' }}>
                    {/* {acceptedFields[e?.name] === true ? (
                      <CheckCircleOutlined style={{ color: 'green', marginRight: '8px' }} />
                    ) : acceptedFields[e?.name] === false ? (
                      <CloseCircleOutlined style={{ color: 'red', marginRight: '8px' }} />
                    ) : (
                      <InfoCircleOutlined style={{ color: 'blue', marginRight: '8px' }} />
                    )} */}
                    {e?.label}
                  </Space>
                }
              >
                {e?.component}
              </Form.Item>
            </Col>
          </React.Fragment>
        ))}
      </Row>
      <Row style={{ marginTop: 16 }}>
        {updateConfigInfo && isEditing && (
          <>
            <Col>
              <BaseButton onClick={handleCancelEdit}>Cancel</BaseButton>
            </Col>
            <Col>
              <BaseButton
                style={{ marginLeft: 8 }}
                type="primary"
                onClick={handleUpdateConfig}
                loading={disabledButtons.update}
              >
                Save Config Info
              </BaseButton>
            </Col>
          </>
        )}
      </Row>
    </>
  );
};

export default Configuration;
